import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RichTextarea } from "@/components/ui/RichTextarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { 
  Plus, Trash2, Edit2, MessageCircle, AlertCircle, Bot, Pointer, CheckCircle, XCircle, Clock 
} from "lucide-react";

export type BubbleType = "modal" | "toast" | "chatbot" | "tooltip";
export type BubbleShape = "rounded" | "square" | "pill";
export type BubbleTrigger = "immediate" | "scroll" | "delay";
export type BubblePosition = "center" | "top-right" | "top-left" | "bottom-right" | "bottom-left" | "top-center" | "bottom-center";
export type BubbleStatus = "draft" | "published";
export type BubbleSize = "sm" | "md" | "lg" | "xl";
export type BubbleTextFormat = "normal" | "bold" | "italic" | "bold-italic";

export interface Bubble {
  id: string;
  type: BubbleType;
  title: string;
  message: string;
  shape: BubbleShape;
  colorClass: string; 
  triggerMode: BubbleTrigger;
  triggerDelay?: number;
  position: BubblePosition;
  active: boolean;
  status: BubbleStatus; 
  targetPage: string;
  imageUrl?: string;
  linkUrl?: string;
  buttonText?: string;
  createdAt: string;
  startDate?: string;
  endDate?: string;
  bubbleSize?: BubbleSize;
  textFormat?: BubbleTextFormat;
}

const colorOptions = [
  { value: "bg-primary", label: "Bleu Principal (Primary)" },
  { value: "bg-secondary", label: "Orange (Secondary)" },
  { value: "bg-accent", label: "Doré (Accent)" },
  { value: "bg-destructive", label: "Rouge Alerte (Destructive)" },
  { value: "bg-background", label: "Blanc / Standard" },
  { value: "bg-muted", label: "Gris clair (Muted)" },
];

const AdminBullesPage = () => {
  const { toast } = useToast();
  
  // State
  const [bulles, setBulles] = useState<Bubble[]>(() => {
    const saved = localStorage.getItem("kaynin_bulles");
    return saved ? JSON.parse(saved) : [];
  });
  
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [previewActive, setPreviewActive] = useState(false);
  
  const initialForm: Omit<Bubble, "id" | "active" | "status" | "createdAt"> = {
    type: "toast",
    title: "",
    message: "",
    shape: "rounded",
    colorClass: "bg-primary",
    triggerMode: "immediate",
    triggerDelay: 3,
    position: "bottom-right",
    targetPage: "all",
    imageUrl: "",
    linkUrl: "",
    buttonText: "J'ai compris",
    startDate: "",
    endDate: "",
    bubbleSize: "md",
    textFormat: "normal"
  };

  const [formData, setFormData] = useState(initialForm);

  // Sync to local storage
  useEffect(() => {
    localStorage.setItem("kaynin_bulles", JSON.stringify(bulles));
    window.dispatchEvent(new Event("kaynin_bulles_updated"));
  }, [bulles]);

  const handleSave = (status: BubbleStatus) => {
    if (!formData.title && !formData.message && !formData.imageUrl) {
      toast({ title: "Erreur", description: "Veuillez fournir au moins un titre, un message ou une image.", variant: "destructive" });
      return;
    }

    if (editingId) {
      setBulles(bulles.map(b => b.id === editingId ? { ...b, ...formData, status } : b));
      toast({ title: "Modifiée", description: `La bulle a été modifiée en tant que ${status}.` });
    } else {
      const newBulb: Bubble = {
        id: Math.random().toString(36).substr(2, 9),
        ...formData,
        active: status === "published",
        status,
        createdAt: new Date().toISOString()
      };
      setBulles([newBulb, ...bulles]);
      toast({ title: "Créée", description: `La bulle a été créée en tant que ${status}.` });
    }
    
    setShowForm(false);
    setEditingId(null);
    setFormData(initialForm);
  };

  const handleDelete = (id: string) => {
    setBulles(bulles.filter(b => b.id !== id));
    toast({ title: "Supprimée", description: "La bulle a été supprimée." });
  };

  const handleEdit = (bubble: Bubble) => {
    setFormData(bubble);
    setEditingId(bubble.id);
    setShowForm(true);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData({ ...formData, imageUrl: event.target?.result as string });
    };
    reader.readAsDataURL(file);
  };

  const toggleActive = (id: string) => {
    setBulles(bulles.map(b => {
      if (b.id === id) {
         if (b.status === "draft" && !b.active) {
            toast({ title: "Action impossible", description: "Veuillez d'abord publier cette bulle avant de l'activer.", variant: "destructive" });
            return b;
         }
         // Si on l'active, on le retire des bulles fermées de la session pour pouvoir le revoir en live
         if (!b.active) {
            const closedInSession = JSON.parse(sessionStorage.getItem("kaynin_closed_bubbles") || "[]");
            sessionStorage.setItem("kaynin_closed_bubbles", JSON.stringify(closedInSession.filter((bId: string) => bId !== id)));
         }
         return { ...b, active: !b.active };
      }
      return b;
    }));
  };

  const getIconForType = (type: BubbleType) => {
    switch (type) {
      case "modal": return <AlertCircle className="w-5 h-5 text-red-500" />;
      case "toast": return <MessageCircle className="w-5 h-5 text-blue-500" />;
      case "chatbot": return <Bot className="w-5 h-5 text-emerald-500" />;
      case "tooltip": return <Pointer className="w-5 h-5 text-amber-500" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-card p-6 rounded-2xl shadow-sm border border-border">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Gestion des Bulles UI</h1>
          <p className="text-muted-foreground font-body">Gérez les pop-ups, chatbots, toasts et infobulles du système.</p>
        </div>
        <Button onClick={() => { setFormData(initialForm); setEditingId(null); setShowForm(!showForm); }} className="gap-2 font-semibold">
          {showForm ? <XCircle className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? "Annuler" : "Créer une Bulle"}
        </Button>
      </div>

      {showForm && (
        <Card className="border-primary/20 shadow-lg">
          <CardHeader className="bg-muted/30 border-b border-border/50">
            <CardTitle>{editingId ? "Modifier la bulle" : "Nouvelle Bulle Dynamique"}</CardTitle>
            <CardDescription>Configurez le comportement et l'apparence de votre notification.</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label>Type de Bulle</Label>
                <Select value={formData.type} onValueChange={(v: BubbleType) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="toast">Notification (Toast / Snackbar)</SelectItem>
                    <SelectItem value="modal">Fenêtre Modale (Pop-up central)</SelectItem>
                    <SelectItem value="chatbot">Widget Chatbot (Bulle d'interaction)</SelectItem>
                    <SelectItem value="tooltip">Infobulle (Tooltip pour guidage)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Titre (Interne ou affiché)</Label>
                <Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="Ex: Alerte Maintenance" />
              </div>

              <div className="space-y-3 md:col-span-2">
                <Label>Message Principal</Label>
                <RichTextarea value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} placeholder="Le texte qui sera affiché dans la bulle..." className="min-h-[100px]" />
              </div>

              <div className="space-y-3 md:col-span-2">
                <Label>Texte du bouton d'action (Modales & Toasts)</Label>
                <div className="flex flex-col gap-2">
                  <Input value={formData.buttonText !== undefined ? formData.buttonText : "J'ai compris"} onChange={e => setFormData({ ...formData, buttonText: e.target.value })} placeholder="Ex: J'ai compris (Laissez vide pour aucun bouton)" />
                  <div className="flex gap-2 text-xs flex-wrap">
                    <Badge variant="outline" className="cursor-pointer hover:bg-muted" onClick={() => setFormData({...formData, buttonText: "J'ai compris"})}>J'ai compris</Badge>
                    <Badge variant="outline" className="cursor-pointer hover:bg-muted" onClick={() => setFormData({...formData, buttonText: "À découvrir"})}>À découvrir</Badge>
                    <Badge variant="outline" className="cursor-pointer hover:bg-muted" onClick={() => setFormData({...formData, buttonText: "À consulter"})}>À consulter</Badge>
                    <Badge variant="outline" className="cursor-pointer hover:bg-muted text-muted-foreground" onClick={() => setFormData({...formData, buttonText: ""})}>Vierge (Aucun bouton)</Badge>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Position à l'écran</Label>
                <Select value={formData.position} onValueChange={(v: BubblePosition) => setFormData({ ...formData, position: v })}>
                  <SelectTrigger><SelectValue placeholder="Position" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="center">Au Centre (Idéal Modale)</SelectItem>
                    <SelectItem value="bottom-right">En bas à droite</SelectItem>
                    <SelectItem value="bottom-left">En bas à gauche</SelectItem>
                    <SelectItem value="top-right">En haut à droite</SelectItem>
                    <SelectItem value="top-center">En haut au centre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Déclenchement (Trigger)</Label>
                <Select value={formData.triggerMode} onValueChange={(v: BubbleTrigger) => setFormData({ ...formData, triggerMode: v })}>
                  <SelectTrigger><SelectValue placeholder="Quand s'affiche-t-elle ?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">Immédiatement (Chargement page)</SelectItem>
                    <SelectItem value="delay">Après un délai</SelectItem>
                    <SelectItem value="scroll">Après un peu de défilement (Scroll)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.triggerMode === "delay" && (
                <div className="space-y-3">
                  <Label>Délai en secondes</Label>
                  <Input type="number" min="1" value={formData.triggerDelay} onChange={e => setFormData({ ...formData, triggerDelay: Number(e.target.value) })} />
                </div>
              )}

              <div className="space-y-3">
                <Label>Forme</Label>
                <Select value={formData.shape} onValueChange={(v: BubbleShape) => setFormData({ ...formData, shape: v })}>
                  <SelectTrigger><SelectValue placeholder="Forme" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rounded">Bords arrondis (Classique)</SelectItem>
                    <SelectItem value="pill">Gélule (Très arrondi)</SelectItem>
                    <SelectItem value="square">Carré strcit</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Couleur et Style</Label>
                <Select value={formData.colorClass} onValueChange={(v) => setFormData({ ...formData, colorClass: v })}>
                  <SelectTrigger><SelectValue placeholder="Couleur" /></SelectTrigger>
                  <SelectContent>
                    {colorOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Taille de la Bulle</Label>
                <Select value={formData.bubbleSize || "md"} onValueChange={(v: BubbleSize) => setFormData({ ...formData, bubbleSize: v })}>
                  <SelectTrigger><SelectValue placeholder="Taille..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sm">Petit (Small)</SelectItem>
                    <SelectItem value="md">Moyen (Medium - Défaut)</SelectItem>
                    <SelectItem value="lg">Grand (Large)</SelectItem>
                    <SelectItem value="xl">Très Grand (Extra Large)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Format du Texte</Label>
                <Select value={formData.textFormat || "normal"} onValueChange={(v: BubbleTextFormat) => setFormData({ ...formData, textFormat: v })}>
                  <SelectTrigger><SelectValue placeholder="Format..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="bold">Gras</SelectItem>
                    <SelectItem value="italic">Italique</SelectItem>
                    <SelectItem value="bold-italic">Gras & Italique</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-3">
                <Label>Image Facultative (PC/Mobile)</Label>
                <div className="flex items-center gap-2">
                  <Input type="file" accept="image/*" onChange={handleImageUpload} className="w-full" />
                  {formData.imageUrl && <Button variant="ghost" onClick={() => setFormData({...formData, imageUrl: ""})}><XCircle className="w-4 h-4 text-destructive"/></Button>}
                </div>
              </div>
              
              <div className="space-y-3">
                <Label>Lien Hypertexte (Optionnel)</Label>
                <Input value={formData.linkUrl || ''} onChange={e => setFormData({ ...formData, linkUrl: e.target.value })} placeholder="https://..." />
              </div>
              
              <div className="grid grid-cols-2 gap-4 md:col-span-2">
                <div className="space-y-3">
                  <Label>Date de mise en ligne (Optionnelle)</Label>
                  <Input type="datetime-local" value={formData.startDate || ''} onChange={e => setFormData({ ...formData, startDate: e.target.value })} />
                </div>
                <div className="space-y-3">
                  <Label>Date de désactivation (Optionnelle)</Label>
                  <Input type="datetime-local" value={formData.endDate || ''} onChange={e => setFormData({ ...formData, endDate: e.target.value })} />
                </div>
              </div>

              <div className="space-y-3">
                <Label>Cible d'affichage</Label>
                <Select value={formData.targetPage} onValueChange={(v) => setFormData({ ...formData, targetPage: v })}>
                  <SelectTrigger><SelectValue placeholder="Où afficher la bulle ?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">🌐 Tout le site (Toutes les pages)</SelectItem>
                    <SelectItem value="/">🏠 Accueil uniquement</SelectItem>
                    <SelectItem value="/voter">🗳️ Espace Électeurs (/voter)</SelectItem>
                    <SelectItem value="/engagement">🤝 Actions Citoyennes (/engagement)</SelectItem>
                    <SelectItem value="/candidats">👤 Candidats (/candidats)</SelectItem>
                    <SelectItem value="/programmes">📜 Programmes (/programmes)</SelectItem>
                    <SelectItem value="/tv">📺 Kaynin Web TV (/tv)</SelectItem>
                    <SelectItem value="/barometre">📊 Baromètre d'Accréditation (/barometre)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-4 pt-6 mt-4 border-t border-border">
              <Button variant="outline" className="mr-auto text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => setPreviewActive(true)}>
                <Pointer className="w-4 h-4 mr-2" /> Prévisualiser
              </Button>
              <Button variant="outline" onClick={() => handleSave("draft")}>Sauvegarder en Brouillon</Button>
              <Button onClick={() => handleSave("published")}>{editingId ? "Mettre à jour & Publier" : "Publier immédiatement"}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des bulles */}
      <div className="grid gap-4 mt-6">
        {bulles.length === 0 ? (
          <div className="text-center py-20 bg-card rounded-2xl border border-border">
            <MessageCircle className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-xl font-display font-medium text-foreground">Aucune bulle configurée</h3>
            <p className="text-muted-foreground mt-2">Créez votre première bulle pour interagir avec les utilisateurs.</p>
          </div>
        ) : (
          Object.entries(
            bulles.reduce((acc, bubble) => {
              const d = new Date(bubble.createdAt);
              if (isNaN(d.getTime())) return acc;
              const key = `${d.toLocaleString('fr-FR', { month: 'long' })} ${d.getFullYear()}`.toUpperCase();
              if (!acc[key]) acc[key] = [];
              acc[key].push(bubble);
              return acc;
            }, {} as Record<string, Bubble[]>)
          ).map(([monthYear, groupBulles]) => (
            <div key={monthYear} className="mb-6">
              <h3 className="font-display font-bold text-muted-foreground mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-primary/50"></span>
                {monthYear}
              </h3>
              <div className="grid gap-4">
                {groupBulles.map(bubble => (
                  <Card key={bubble.id} className={`transition-all ${!bubble.active ? "opacity-75 grayscale-[0.2]" : "border-primary/20 shadow-sm"}`}>
                    <div className="p-5 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                      
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl bg-background border border-border shadow-sm flex flex-col gap-2 items-center justify-center`}>
                          {getIconForType(bubble.type)}
                        </div>
                        <div>
                          <h3 className="font-display font-bold text-foreground capitalize flex items-center gap-2">
                             {bubble.title} 
                             {bubble.status === "draft" && <Badge variant="secondary" className="text-xs font-normal">Brouillon</Badge>}
                             <Badge variant="outline" className="capitalize text-xs font-normal">{bubble.type}</Badge>
                          </h3>
                          <p className="text-sm text-foreground/70 font-body line-clamp-1 max-w-lg mt-1">{bubble.message}</p>
                          <div className="flex gap-3 text-xs text-muted-foreground mt-2 flex-wrap">
                             <span><strong>Position:</strong> {bubble.position}</span>
                             <span><strong>Cible:</strong> {bubble.targetPage}</span>
                             <span><strong>Couleur:</strong> {bubble.colorClass}</span>
                             {bubble.startDate && <span><strong>Du:</strong> {new Date(bubble.startDate).toLocaleDateString('fr-FR')}</span>}
                             {bubble.endDate && <span><strong>Au:</strong> {new Date(bubble.endDate).toLocaleDateString('fr-FR')}</span>}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-6 md:ml-auto">
                         <div className="flex flex-col items-center gap-2 min-w-[80px]">
                           <Switch 
                             checked={bubble.active} 
                             onCheckedChange={() => toggleActive(bubble.id)} 
                             disabled={bubble.status === 'draft'}
                           />
                           <span className={`text-[10px] uppercase tracking-wider font-bold ${bubble.active ? 'text-primary' : 'text-muted-foreground'}`}>
                             {bubble.active ? "Activée" : "Désactivée"}
                           </span>
                         </div>
                         
                         <div className="flex gap-2">
                           <Button variant="ghost" size="icon" onClick={() => handleEdit(bubble)} className="text-blue-500 hover:text-blue-600 hover:bg-blue-50/50">
                             <Edit2 className="w-4 h-4" />
                           </Button>
                           <Button variant="ghost" size="icon" onClick={() => handleDelete(bubble.id)} className="text-destructive hover:bg-destructive/10">
                             <Trash2 className="w-4 h-4" />
                           </Button>
                         </div>
                       </div>
                    </div>
                  </Card>
                 ))}
              </div>
            </div>
          ))
        )}
      </div>

      {previewActive && (
        <BubblePreview formData={formData} onClose={() => setPreviewActive(false)} />
      )}
    </div>
  );
};

const BubblePreview = ({ formData, onClose }: { formData: Omit<Bubble, "id" | "active" | "status" | "createdAt">, onClose: () => void }) => {
  const getPositionClasses = () => {
    switch (formData.position) {
      case "center": return "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2";
      case "top-right": return "top-8 right-8";
      case "top-left": return "top-8 left-8";
      case "bottom-right": return "bottom-8 right-8";
      case "bottom-left": return "bottom-8 left-8";
      case "top-center": return "top-8 left-1/2 -translate-x-1/2";
      case "bottom-center": return "bottom-8 left-1/2 -translate-x-1/2";
      default: return "bottom-8 right-8";
    }
  };

  const getShapeClass = () => {
    switch (formData.shape) {
      case "pill": return "rounded-full";
      case "square": return "rounded-none";
      default: return "rounded-2xl";
    }
  };

  const isDarkBase = formData.colorClass !== "bg-background" && formData.colorClass !== "bg-muted";
  const textColor = isDarkBase ? "text-white" : "text-foreground";

  const [visible, setVisible] = useState(formData.triggerMode !== "delay");
  useEffect(() => {
    if (formData.triggerMode === "delay" && formData.triggerDelay) {
      const timer = setTimeout(() => setVisible(true), formData.triggerDelay * 1000);
      return () => clearTimeout(timer);
    }
  }, [formData]);

  if (!visible) {
    return (
      <div className="fixed inset-0 z-[100] pointer-events-none flex items-center justify-center bg-black/5 backdrop-blur-[1px]">
         <div className="bg-background px-4 py-2 rounded-xl shadow-lg border border-border animate-pulse text-sm font-semibold flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" /> Simulation du délai de {formData.triggerDelay}s...
         </div>
      </div>
    );
  }

  const hasBackdrop = formData.type === "modal" || formData.position === "center";
  
  const getWidthClass = () => {
    switch(formData.bubbleSize) {
      case 'sm': return 'max-w-xs';
      case 'lg': return 'max-w-md';
      case 'xl': return 'max-w-xl';
      default: return 'max-w-sm'; // md
    }
  };

  const textClasses = `text-balance ${formData.bubbleSize === 'lg' || formData.bubbleSize === 'xl' ? 'text-base' : 'text-sm'} ${formData.textFormat === 'bold' || formData.textFormat === 'bold-italic' ? 'font-bold' : ''} ${formData.textFormat === 'italic' || formData.textFormat === 'bold-italic' ? 'italic' : ''}`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
       {hasBackdrop && (
         <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity animate-in fade-in" onClick={onClose} />
       )}
       
       <div className="-mt-16 w-full h-full relative pointer-events-none">
       {formData.type === "chatbot" ? (
         <div className={`absolute ${getPositionClasses()} ${getShapeClass()} pointer-events-auto flex items-center justify-center w-16 h-16 shadow-2xl ${formData.colorClass} ${textColor} cursor-pointer hover:scale-105 transition-all duration-300 animate-in zoom-in group`} onClick={onClose}>
            <Bot className="w-8 h-8" />
            <div className={`absolute ${formData.position.includes('left') ? 'left-[calc(100%+1rem)]' : 'right-[calc(100%+1rem)]'} top-1/2 -translate-y-1/2 bg-background ${isDarkBase ? 'text-foreground' : ''} border border-border/50 shadow-xl p-4 rounded-2xl whitespace-nowrap min-w-[250px] opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-0 ${formData.position.includes('left') ? '-translate-x-2' : 'translate-x-2'}`}>
               <p className="text-sm font-bold mb-1 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-primary"/>{formData.title}</p>
               <div className="text-xs text-muted-foreground whitespace-normal leading-relaxed" dangerouslySetInnerHTML={{ __html: formData.message }} />
               <div className="mt-3 text-[10px] text-center bg-primary/10 text-primary py-1 rounded-md font-bold uppercase tracking-wider">Cliquez la bulle pour fermer</div>
            </div>
         </div>
       ) : formData.type === "tooltip" ? (
         <div className={`absolute ${getPositionClasses()} flex items-center gap-3 pointer-events-auto animate-in fade-in slide-in-from-bottom-2`}>
            <div className={`w-10 h-10 ${getShapeClass()} ${formData.colorClass} ${textColor} flex items-center justify-center shadow-lg hover:animate-pulse cursor-pointer transition-transform hover:scale-105`}>
              <Pointer className="w-5 h-5" />
            </div>
            <div className={`bg-background text-foreground border border-border/50 shadow-xl p-3 ${getShapeClass() === 'rounded-full' ? 'rounded-2xl' : getShapeClass()} text-sm max-w-[250px] space-y-1`}>
               <p className="font-bold flex justify-between items-center">{formData.title} <Button variant="ghost" size="icon" onClick={onClose} className="h-5 w-5 -mr-1 -mt-1 rounded-full opacity-50 hover:opacity-100"><XCircle className="w-4 h-4" /></Button></p>
               <div className="text-muted-foreground text-xs leading-relaxed" dangerouslySetInnerHTML={{ __html: formData.message }} />
            </div>
         </div>
       ) : (
         <div className={`absolute ${getPositionClasses()} ${getShapeClass()} ${formData.colorClass} ${textColor} p-6 shadow-2xl ${getWidthClass()} w-full pointer-events-auto transition-all animate-in zoom-in-95 duration-300 border border-white/10 ${hasBackdrop ? 'scale-100' : ''}`}>
            <div className="flex justify-between items-start mb-3">
              <h4 className="font-display font-bold text-lg flex items-center gap-2">
                {formData.type === 'modal' ? <AlertCircle className="w-5 h-5 opacity-80" /> : <MessageCircle className="w-5 h-5 opacity-80" />}
                {formData.title}
              </h4>
              <button onClick={onClose} className="opacity-50 hover:opacity-100 transition-opacity p-1 hover:bg-black/10 rounded-full">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            {formData.imageUrl && (
              <div className="mb-3 w-full rounded overflow-hidden border border-border/50">
                <img src={formData.imageUrl} className="w-full h-auto object-cover max-h-48" alt="Bulle media" />
              </div>
            )}
            <div 
              className={`font-body ${isDarkBase ? 'text-white/80' : 'text-muted-foreground'} leading-relaxed mb-1 ${textClasses}`}
              dangerouslySetInnerHTML={{ __html: formData.message }}
            />
            {formData.type === "modal" && (
              <div className="mt-6 flex justify-end gap-3 w-full">
                <Button variant={isDarkBase ? "secondary" : "default"} onClick={onClose} className="w-full sm:w-auto font-bold">J'ai compris</Button>
              </div>
            )}
         </div>
       )}
       </div>
       
       <div className="fixed top-6 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-2 rounded-full font-bold shadow-lg shadow-orange-500/20 text-sm tracking-widest flex items-center gap-2 z-[101] backdrop-blur-sm animate-in slide-in-from-top-4 border border-white/20">
         <Pointer className="w-4 h-4" /> MODE PRÉVISUALISATION
       </div>
    </div>
  );
};

export default AdminBullesPage;
